import type { V2 } from '../../core/math';
import type { ZoneOpts } from '../../game/netHooks';
import type { ZoneRuntime, ZoneScript } from '../../game/ZoneRuntime';
import type { Actor } from '../../sim/actor';
import type { SimEvent } from '../../sim/events';
import type { World } from '../../sim/world';
import type { DeathView, HudAction, HudView, InstanceEnv, ResultView } from './env';

/** Script-message channel key (other zone scripts ignore our messages). */
export const CHANNEL = 'inst';

export interface ScriptState {
  /** Bumped by the host on every change. */
  seq: number;
}

export type Msg = Record<string, unknown>;

const isObj = (v: unknown): v is Msg => typeof v === 'object' && v !== null && !Array.isArray(v);
export const num = (v: unknown, d = 0): number => (typeof v === 'number' && Number.isFinite(v) ? v : d);
export const str = (v: unknown, d = ''): string => (typeof v === 'string' ? v : d);

/**
 * Zone options for the next zone of a run (Rift floor, raid gate). Run keys
 * (difficulty, run id, party size, password) carry over; a group run gets a
 * fresh instance room per zone (`<root>:<zone>`) hosted by the current host,
 * so peers still loading the previous zone never mix with the new one.
 */
export function nextZoneOpts(opts: ZoneOpts, zone: string, host: string, extra: ZoneOpts = {}): ZoneOpts {
  const out: ZoneOpts = {};
  for (const [k, v] of Object.entries(opts)) if (k !== 'inst' && k !== 'host' && k !== 'root') out[k] = v;
  const root = str(opts.root) || str(opts.inst);
  if (root) {
    out.root = root;
    out.inst = `${root}:${zone}`;
    out.host = host;
  }
  return { ...out, ...extra };
}

/**
 * Base class for instance scripts, following the multiplayer contract
 * (docs/AGENT_BRIEF.md): every peer runs the script; the zone host owns the
 * plain-data `state` and mirrors it to the others (on every change and once
 * a second), so a migrated host continues from the last mirrored state.
 * Non-hosts extrapolate timers between updates and react to state changes
 * through `changed()` exactly like the host does — rewards are granted by
 * each peer for its own character when the host's state says so.
 */
export abstract class InstanceScript<S extends ScriptState> implements ZoneScript {
  z!: ZoneRuntime;
  state: S;
  /** Local simulation seconds since the script started. */
  protected clock = 0;
  /** Full-state broadcast interval (changes are sent right away). */
  protected syncEvery = 1;
  private dirty = false;
  private syncT = 0;
  private timers: { at: number; fn: () => void }[] = [];
  private cleanups: (() => void)[] = [];
  private localRun: string | null = null;

  constructor(
    readonly env: InstanceEnv,
    readonly opts: ZoneOpts,
    initial: S,
  ) {
    this.state = initial;
  }

  // ---------------------------------------------------------------- accessors

  get world(): World {
    return this.z.world;
  }

  get isHost(): boolean {
    return this.z.world.isHost;
  }

  /** The local player's actor. */
  get me(): Actor | null {
    return this.z.world.actors.get(this.z.localPlayerId) ?? null;
  }

  /**
   * Run id shared by every member: set by the entry UI and carried between
   * floors and gates (falls back to the group instance id, then a local id).
   */
  get run(): string {
    const r = str(this.opts.run) || str(this.opts.root) || str(this.opts.inst);
    if (r) return r;
    this.localRun ??= `${this.z.layout.id}:${Math.floor(Math.random() * 1e9).toString(36)}`;
    return this.localRun;
  }

  spawnPoint(name: string): V2 & { facing?: number } {
    return this.z.layout.spawnPoints[name] ?? this.z.layout.spawnPoints.start ?? { x: 0, z: 0 };
  }

  players(): Actor[] {
    const out: Actor[] = [];
    for (const a of this.world.actors.values()) if (a.kind === 'player') out.push(a);
    return out;
  }

  livingPlayers(): Actor[] {
    return this.players().filter((a) => a.alive);
  }

  /** Monsters carrying `tag` (alive only unless `all`). */
  tagged(tag: string, all = false): Actor[] {
    const out: Actor[] = [];
    for (const a of this.world.actors.values()) if (a.monster?.tags.includes(tag) && (all || a.alive)) out.push(a);
    return out;
  }

  // ---------------------------------------------------------------- ZoneScript

  start(z: ZoneRuntime): void {
    this.z = z;
    this.init();
    if (this.isHost) this.hostInit();
    this.dirty = true;
  }

  tick(_z: ZoneRuntime, dt: number): void {
    this.clock += dt;
    if (this.timers.length) {
      const due = this.timers.filter((t) => t.at <= this.clock);
      if (due.length) {
        this.timers = this.timers.filter((t) => t.at > this.clock);
        for (const t of due) t.fn();
      }
    }
    if (this.isHost) {
      this.hostTick(dt);
      this.syncT -= dt;
      if (this.dirty || this.syncT <= 0) this.broadcast();
    } else this.mirrorTick(dt);
    this.localTick(dt);
  }

  event(_z: ZoneRuntime, ev: SimEvent): void {
    if (this.isHost) this.hostEvent(ev);
    this.localEvent(ev);
  }

  message(_z: ZoneRuntime, from: string, data: Msg): void {
    if (data.ch !== CHANNEL) return;
    if (data.t === 'st') {
      if (!this.isHost && from === this.world.hostPeer && isObj(data.s)) this.adopt(data.s as unknown as S);
      return;
    }
    if (this.isHost) this.request(from, data);
    else if (from === this.world.hostPeer) this.notice(data);
  }

  prerender(_z: ZoneRuntime, dt: number): void {
    this.present(dt);
  }

  peerJoined(_z: ZoneRuntime, peer: string): void {
    this.broadcast(peer);
  }

  hostChanged(_z: ZoneRuntime, _host: string): void {
    if (!this.isHost) return;
    this.becameHost();
    this.dirty = true;
  }

  dispose(_z: ZoneRuntime): void {
    for (const f of this.cleanups.splice(0)) f();
    this.disposed();
  }

  // ---------------------------------------------------------------- hooks

  /** Every peer, once at start (read opts, local bookkeeping, visuals). */
  protected init(): void {}
  /** Host only, once at start (spawns). */
  protected hostInit(): void {}
  protected hostTick(_dt: number): void {}
  /** Non-hosts: extrapolate timers between state updates. */
  protected mirrorTick(_dt: number): void {}
  /** Every peer, every tick (local player checks). */
  protected localTick(_dt: number): void {}
  protected hostEvent(_ev: SimEvent): void {}
  protected localEvent(_ev: SimEvent): void {}
  /** Host: a peer's request (`toHost`). */
  protected request(_from: string, _data: Msg): void {}
  /** Peer: a notice from the host (`toPeer`). */
  protected notice(_data: Msg): void {}
  /** This peer just became the host (migration): rebuild host-side bookkeeping. */
  protected becameHost(): void {}
  /** Every peer, on every state change (host: `update`, others: adopted from the host). */
  protected changed(_prev: S, _next: S): void {}
  protected disposed(): void {}
  /** Presentation update before each rendered frame (never called headless). */
  protected present(_dt: number): void {}

  /** HUD panel contents (null: no instance HUD in this zone). */
  abstract view(): HudView | null;
  /** The local player's death screen (null: the game's default applies). */
  deathView(): DeathView | null {
    return null;
  }
  /** End-of-run card (failures, PvP results). */
  resultView(): ResultView | null {
    return null;
  }
  /** A HUD / death / result button was pressed. */
  action(_id: string): void {}

  // ---------------------------------------------------------------- helpers

  /** Host: applies a state change, notifies every local hook and mirrors it. */
  protected update(patch: Partial<S>): void {
    const prev = this.state;
    const next = { ...prev, ...patch, seq: prev.seq + 1 };
    this.state = next;
    this.dirty = true;
    this.changed(prev, next);
  }

  private adopt(next: S): void {
    const prev = this.state;
    this.state = next;
    this.changed(prev, next);
  }

  /** Host: a cheap change (gauge, counters) — mirror it within a quarter second. */
  protected soon(): void {
    this.syncT = Math.min(this.syncT, 0.25);
  }

  protected broadcast(to?: string): void {
    this.dirty = false;
    this.syncT = this.syncEvery;
    this.z.net?.send({ ch: CHANNEL, t: 'st', s: this.state }, to);
  }

  /** Sends a request to the host (handled locally when this peer is the host). */
  protected toHost(t: string, data: Msg = {}): void {
    const msg = { ...data, ch: CHANNEL, t };
    if (this.isHost) this.request(this.world.localPeer, msg);
    else this.z.net?.send(msg, this.world.hostPeer);
  }

  /** Host: sends a notice to one peer (handled locally when it is this peer). */
  protected toPeer(peer: string, t: string, data: Msg = {}): void {
    const msg = { ...data, ch: CHANNEL, t };
    if (peer === this.world.localPeer) this.notice(msg);
    else this.z.net?.send(msg, peer);
  }

  /** Runs `fn` after `seconds` of local simulation time. */
  protected later(seconds: number, fn: () => void): void {
    this.timers.push({ at: this.clock + seconds, fn });
  }

  protected onDispose(fn: () => void): void {
    this.cleanups.push(fn);
  }

  /** Revives the local player (in place, or at a spawn point) and clears the death screen. */
  protected reviveMe(hpPct: number, at?: V2): void {
    const me = this.me;
    if (!me) return;
    if (at) this.placeMe(at);
    if (!me.alive) this.world.revive(me, hpPct);
    else me.hp = Math.max(me.hp, Math.round(me.stats.maxHp * hpPct));
    this.env.revived();
  }

  /** Teleports the local player (owner-side move). */
  protected placeMe(at: V2, facing?: number): void {
    const me = this.me;
    if (!me) return;
    me.pos = { x: at.x, z: at.z };
    me.prevPos = { x: at.x, z: at.z };
    me.path = [];
    me.moveGoal = null;
    me.displacement = null;
    if (facing !== undefined) me.facing = facing;
  }

  /** Removes monsters carrying `tag` without death events (no loot, no kill credit). */
  protected despawnTagged(tag: string): void {
    for (const a of this.tagged(tag, true)) this.world.removeActor(a.id);
  }

  protected leaveAction(confirm?: string): HudAction {
    return confirm ? { id: 'leave', label: 'Leave', tone: 'plain', confirm } : { id: 'leave', label: 'Leave', tone: 'plain' };
  }
}
